import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { EliminationMatrixResultView } from '@models/eliminationMatrixResultView';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StudyCommentService } from '@services/study-comments.service';
import { StudyComments } from '@models/studyComments.model';
import { DialogService } from '@services/dialog.service';
import * as Constants from '@shared/_constants';
import {
  StudySettingsLookupValues,
  ComparableStatus,
  SectionType,
  StudyStatus,
  PredictionEngineOptions,
  PredictionEngineTooltip,
  DatabaseType,
  LookupType,
} from '../../../_study_constants';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { BDMatrixEliminationCriteria } from '@models/bdMatrixEliminationCriteria.model';
import { BdMatrixService } from '@services/bd-matrix.service';
import { SubsetModel } from '@models/subset.model';
import { SubsetService } from '@services/subset.service';
import { TreeNode } from 'primeng/api';
import { BDMatrix } from '@models/bdMatrix.model';
import { LoggingService } from '@services/logging.service';
import { StudyDetailService } from '@services/study-detail.service';
import { Study } from '@models/study.model';
import * as _ from 'lodash-es';
import { ConfigService } from '@services/config.service';
import * as StudyConstants from '../../../_study_constants';
import { PredictionEngineService } from '../prediction-engine.service';
import { PredictionAcceptance } from './prediction-acceptance.model';
import { ComparableDatabase } from '@models/comparableDatabase.model';
import { ComparableDatabaseFilter } from '@models/bdMatrixFilter.model';
import { NonBvdComparableDetails } from './non-bvd-display/non-bvd-comparable-details.model';
import { LookupService } from '../../../../services/lookup.service';
import { firstValueFrom } from 'rxjs';
export const PREDICTION_NOT_AVAILABLE = 2;

@Component({
  selector: 'app-data-display-dialog',
  templateUrl: './data-display-dialog.component.html',
  styleUrls: ['./data-display-dialog.component.css'],
  providers: [SubsetService],
  encapsulation: ViewEncapsulation.None,
})
export class DataDisplayDialogComponent implements OnInit {
  readOnly: boolean;
  studySettingId: number;
  searchStrategyId: number;
  sectionType: SectionType;
  study: Study;
  comparable: EliminationMatrixResultView;
  blockui = false;

  results: any[] = [];
  decisionCriteriaColumns: BDMatrixEliminationCategory[] = [];
  criteriaColumns: TreeNode[] = [];
  selectedColumns: TreeNode[] = [];
  columnOptions: any[] = [];

  studySettingsLookupValues = StudySettingsLookupValues;
  allSubsets: SubsetModel[] = [];
  formHasChanged = false;
  invalidRejectReason = false;
  invalidDecisionCriteria = false;
  invalidPredictionResultFunctionDecision = false;
  invalidPredictionResultProductDecision = false;
  isRejectionReasonRequired: boolean;
  isUserPredictionDecisionRequired = false;
  internalNoteChanged = false;

  comparableFunctionDisplayOrderNumber = 1;
  comparableProductDisplayOrderNumber = 2;
  predictionEngineOptions = PredictionEngineOptions;
  predictionEngineTooltip = PredictionEngineTooltip;
  databaseId: number;
  comparableDatabases: ComparableDatabase[] = [];
  selectedDatabase: ComparableDatabase;
  nonBvdComparableDetails: NonBvdComparableDetails;
  isBvd = false;
  isMultiDB = false;
  comparableTypes: any;
  comparableStatusesMV: any;
  aiLayerEnabled: boolean;

  constructor(
    private activeModal: NgbActiveModal,
    private dialogService: DialogService,
    public bdMatrixService: BdMatrixService,
    private subsetService: SubsetService,
    private studyCommentService: StudyCommentService,
    private studyDetailService: StudyDetailService,
    private logger: LoggingService,
    private configService: ConfigService,
    private predictionEngineService: PredictionEngineService,
    private lookupService: LookupService,
  ) {}

  async ngOnInit() {
    try {
      this.blockui = true;
      this.study = this.studyDetailService.study;
      this.aiLayerEnabled = this.bdMatrixService.aiLayerEnabledStatus();
      await this.initializeForm();
      this.isRejectionReasonRequired = await firstValueFrom(this.configService.getConfigValueBoolean(ConfigService.IS_REJECTION_REASON_REQUIRED));
    } finally {
      this.blockui = false;
    }
  }

  async close() {
    if (this.formHasChanged) {
      const confirmationResult = await this.dialogService.confirm('Please Confirm', Constants.ConfirmationMessage.UnsavedModifications, 'Yes', 'No');
      if (confirmationResult === Constants.ConfirmationResult.yes) {
        this.activeModal.close(null);
      }
    } else {
      this.activeModal.close(null);
    }
  }

  get showTranlateDescriptionButton(): boolean {
    if (this.studySettingsLookupValues.TradeDescription === this.studySettingId || this.studySettingsLookupValues.Both === this.studySettingId) {
      if (
        this.comparable &&
        _.isEmpty(this.comparable.tradeDescTranslated) &&
        !_.isEmpty(this.comparable.tradeDescNL) &&
        _.isEmpty(this.comparable.tradeDescEN)
      ) {
        return true;
      }
    }
    return false;
  }

  translateDescription() {
    this.blockui = true;
    this.bdMatrixService.TranslateDescription(this.searchStrategyId, this.comparable.comparableID).subscribe((tranDesc) => {
      this.comparable.tradeDescTranslated = tranDesc;
      this.blockui = false;
    });
  }

  async save() {
    try {
      this.blockui = true;
      if (this.validateForm()) {
        await this.saveComparable(this.comparable);

        if (this.formHasChanged && this.internalNoteChanged) {
          await this.saveInternalNotes();
        }

        await this.saveAcceptanceOfPrediction();

        this.formHasChanged = false;
        this.blockui = false;
        this.activeModal.close(this.comparable);
      }
    } finally {
      this.blockui = false;
    }
  }

  async loadPrevious() {
    try {
      this.blockui = true;
      this.comparableDatabases = [];
      // save changes
      if (!this.readOnly && this.validateForm(false)) {
        await this.SaveComparableOnPagination();
      }
      if (this.bdMatrixService.itemIndex > 0) {
        this.bdMatrixService.comparableIndex -= 1;
        await this.getBdMatrixItem();
        await this.getComparableDatabases();
        this.getCriteriaColumns(this.readOnly);
      }
      this.formHasChanged = false;
    } finally {
      this.blockui = false;
    }
  }

  async loadNext() {
    try {
      this.blockui = true;
      this.comparableDatabases = [];
      // save changes
      if (!this.readOnly && this.validateForm(false)) {
        await this.SaveComparableOnPagination();
      }
      if (this.bdMatrixService.itemIndex < this.bdMatrixService.comparableCount) {
        this.bdMatrixService.comparableIndex += 1;
        await this.getBdMatrixItem();
        await this.getComparableDatabases();
        this.getCriteriaColumns(this.readOnly);
      }
      this.formHasChanged = false;
    } finally {
      this.blockui = false;
    }
  }
  async SaveComparableOnPagination(): Promise<void> {
    await this.saveComparable(this.comparable);
    if (this.formHasChanged && this.internalNoteChanged) {
      await this.saveInternalNotes();
    }

    await this.saveAcceptanceOfPrediction();
  }

  removeFromSubset(): void {
    this.comparable.bdMatrixSubsetId = null;
    this.formChanged();
  }

  get isAccepted(): boolean {
    return this.comparable.comparableStatusId === ComparableStatus.Accepted;
  }

  get isRejected(): boolean {
    return this.comparable.comparableStatusId === ComparableStatus.Rejected;
  }

  get isNeutral(): boolean {
    return this.comparable.comparableStatusId === ComparableStatus.Neutral;
  }

  setAccepted(): void {
    this.comparable.comparableStatusId = ComparableStatus.Accepted;
    this.isUserPredictionDecisionRequired = true;
    this.formChanged();
  }

  setRejected(): void {
    this.comparable.comparableStatusId = ComparableStatus.Rejected;
    this.isUserPredictionDecisionRequired = true;
    this.formChanged();
  }

  setNeutral(): void {
    this.comparable.comparableStatusId = ComparableStatus.Neutral;
    this.isUserPredictionDecisionRequired = true;
    this.formChanged();
  }

  setReviewedFlag(): void {
    this.formChanged();
  }

  getFunctionPredictionClassificationType(): string {
    return Constants.FunctionPredictionClassificationTypes[this.comparable.functionPrediction];
  }

  get hasFunctionPrediction(): boolean {
    return this.comparable.functionPrediction !== PREDICTION_NOT_AVAILABLE;
  }

  getProductPredictionClassificationType(): string {
    return Constants.ProductPredictionClassificationTypes[this.comparable.productPrediction];
  }

  get hasProductPrediction(): boolean {
    return this.comparable.productPrediction !== PREDICTION_NOT_AVAILABLE;
  }

  formChanged(property: string = null): void {
    this.formHasChanged = true;
    if (property === 'rejectReason') {
      this.invalidRejectReason = false;
    } else if (property === 'decisionCriteria') {
      this.invalidDecisionCriteria = false;
      this.isUserPredictionDecisionRequired = true;
    } else if (property === 'funcDecision') {
      this.invalidPredictionResultFunctionDecision = false;
    } else if (property === 'productDecision') {
      this.invalidPredictionResultProductDecision = false;
    } else if (property === 'InternalNotes') {
      this.internalNoteChanged = true;
    }
  }

  private async getSubsets(): Promise<void> {
    this.allSubsets = await this.subsetService.getAllBDMatrixSubset(this.searchStrategyId);
  }

  async saveComparable(comparable: EliminationMatrixResultView) {
    // auto-save the comparable
    if (this.formHasChanged && comparable) {
      await this.bdMatrixService.UpdateBDMatrix(await this.getBDMatrixObject(comparable));

      if (this.study.statusId === StudyStatus.Complete) {
        this.study.statusId = StudyStatus.InProgress;
        this.studyDetailService.SaveStudyStatus();
        await this.studyDetailService.Save();
      }
    }
    const self = this;
    this.criteriaColumns.forEach(function (item) {
      if (item.partialSelected !== (self.comparable[item.data.categoryId] === 1 ? true : false)) {
        self.comparable[item.data.categoryId] = item.partialSelected ? 1 : 0;
        self.saveEliminationCriteria(item.data.categoryId, item.partialSelected);
      }
    });
  }

  async saveInternalNotes() {
    const studyComment = new StudyComments();

    studyComment.referenceId = this.searchStrategyId;
    studyComment.comparableId = this.comparable.comparableID;
    studyComment.comments = this.comparable.comment;
    studyComment.sectionType = this.sectionType;
    return await this.studyCommentService.SaveInternalNotes(studyComment);
  }

  private async getBDMatrixObject(result: EliminationMatrixResultView): Promise<BDMatrix> {
    const bdMatrix = new BDMatrix();
    bdMatrix.comparableStatusId = result.comparableStatusId;
    bdMatrix.bDMatrixId = result.bdMatrixId;
    bdMatrix.comparableId = result.comparableID;
    bdMatrix.studySearchStrategyId = this.searchStrategyId;
    bdMatrix.bDMatrixSubsetId = result.bdMatrixSubsetId > 0 ? result.bdMatrixSubsetId : null;
    bdMatrix.rejectedReason = result.rejectedReason;
    bdMatrix.isReviewed = result.isReviewed;

    return bdMatrix;
  }

  async getCriteriaColumns(readOnly = false) {
    // const filterResults = this.results;
    // const pointer = this.displayResultPointer;
    const self = this;
    this.criteriaColumns = [];
    const criteriaCols = [];
    this.decisionCriteriaColumns.forEach(function (item) {
      let isselected = false;
      if (self.comparable[item.bdMatrixEliminationCategoryId] === 1) {
        isselected = true;
      }
      const treenode: TreeNode = self.convertEliminationCriteriaToTreeNode(item, isselected, readOnly);
      criteriaCols.push(treenode);
    });
    this.criteriaColumns = criteriaCols;
    console.log(this.criteriaColumns);
  }

  private convertEliminationCriteriaToTreeNode(option: BDMatrixEliminationCategory, isselected: boolean, readOnly: boolean): TreeNode {
    const treeNode: TreeNode = {
      label: option.displayName,
      data: { categoryId: option.bdMatrixEliminationCategoryId, displayOrder: option.displayOrder },
      selectable: !readOnly,
      partialSelected: isselected,
    };

    return treeNode;
  }

  private saveEliminationCriteria(eliminationCategoryId: number, isSelected: boolean) {
    // auto-save the elimination criteria-dialog
    const eliminationCriteriaDTO = new BDMatrixEliminationCriteria();
    eliminationCriteriaDTO.bdMatrixId = this.comparable.bdMatrixId;
    eliminationCriteriaDTO.bdMatrixEliminationCategoryId = eliminationCategoryId;
    eliminationCriteriaDTO.isSelected = isSelected;
    this.bdMatrixService.SaveEliminationCriteria(eliminationCriteriaDTO);
    this.comparable[eliminationCategoryId] = isSelected ? 1 : 0;
  }

  private async initializeForm(): Promise<void> {
    this.logger.info(this.constructor.name, 'initializeForm', 'Start');

    const lookupNonBvdComparableTypes = await firstValueFrom(this.lookupService.GetLookupValues$(LookupType[LookupType.NonBvdComparableType]));
    this.comparableTypes = _.keyBy(lookupNonBvdComparableTypes, 'value');
    const lookupNonBvdComparableStatus = await firstValueFrom(this.lookupService.GetLookupValues$(LookupType[LookupType.CompustatCompanyStatus]));
    this.comparableStatusesMV = _.keyBy(lookupNonBvdComparableStatus, 'value');

    this.searchStrategyId = this.bdMatrixService.filter.studySearchStrategyId;

    const result1 = this.getBdMatrixItem();
    const result2 = this.getSubsets();

    this.decisionCriteriaColumns = await this.bdMatrixService.getEliminationCriteriaCategory(this.searchStrategyId);

    await result1;
    await this.getComparableDatabases();
    await this.getCriteriaColumns(this.readOnly);

    await result2;
  }

  private async getBdMatrixItem() {
    this.logger.info(this.constructor.name, 'getBdMatrixItem', 'Start');
    const resultString = await this.bdMatrixService.getItem(this.searchStrategyId);
    const result = JSON.parse(resultString.allResults[0]);

    if (result && result.length === 1) {
      this.comparable = result[0];
      this.databaseId = this.comparable.databaseId;

      const isOneSource = this.databaseId == DatabaseType.OneSource;
      const isCompustat = this.databaseId == DatabaseType.CompustatNA || this.databaseId == DatabaseType.CompustatGlobal;
      this.isBvd = !(isOneSource || isCompustat);

      this.isMultiDB = this.study.searchStrategies[0].studySearchStrategyDatabases.length > 1;

      if (isOneSource || isCompustat) this.nonBvdComparableDetails = this.getNonBvdComparableOverview();

      if (this.showTranlateDescriptionButton) {
        this.bdMatrixService.GetTranslatedDescription(this.searchStrategyId, this.comparable.comparableID).subscribe((tranDesc) => {
          this.comparable.tradeDescTranslated = tranDesc;
        });
      }
    }
  }

  private async getNonBvdComparableDetails() {
    this.logger.info(this.constructor.name, 'getNonBvdComparableDetails', 'Start');

    const db = this.selectedDatabase;
    this.nonBvdComparableDetails = await this.bdMatrixService.getNonBvdComparableDetails(
      db.comparableId,
      db.databaseId,
      db.databaseFeedId,
      this.searchStrategyId,
    );
    this.nonBvdComparableDetails.comparableType = this.comparableTypes[this.comparable.comparableTypeId].displayText;
    this.nonBvdComparableDetails.comparableStatusMV = this.comparableStatusesMV[this.comparable.comparableStatusIdMV].displayText;
  }

  private getNonBvdComparableOverview() {
    const comparableOverview: NonBvdComparableDetails = {
      fullOverview: this.comparable.fullOverviewEN,
      address: this.comparable.address,
      yearIncorporated: this.comparable.yearIncorporated,
      companyWebSite: this.comparable.companyWebSite,
      companyId: this.comparable.bvDId,
      status: this.comparable.status,
      companyName: this.comparable.companyName,
      comparablePhone: this.comparable.comparablePhone,
      countryName: this.comparable.countryName,
      comparableCIK: this.comparable.comparableCIK,
      databaseId: this.comparable.databaseId,
      comparableType: this.comparableTypes[this.comparable.comparableTypeId].displayText,
      comparableStatusMV: this.comparableStatusesMV[this.comparable.comparableStatusIdMV].displayText,
    };

    return comparableOverview;
  }

  private isRejectedReasonInvalid() {
    const otherEleminationCriteriaColumn = this.criteriaColumns.filter(
      (col) => col.data.displayOrder === StudyConstants.ELIMINATION_CRITERIA_OTHER_DISPLAY_ORDER,
    )[0];
    const otherEliminationCriteriaSelected: boolean = otherEleminationCriteriaColumn ? otherEleminationCriteriaColumn.partialSelected : false;

    return (
      this.comparable.comparableStatusId === ComparableStatus.Rejected &&
      (this.isRejectionReasonRequired || otherEliminationCriteriaSelected) &&
      !this.comparable.rejectedReason
    );
  }

  private validateForm(markControlAsInvalid = true) {
    let formValid = true;
    if (this.comparable.comparableStatusId === ComparableStatus.Rejected) {
      if (this.isRejectedReasonInvalid()) {
        formValid = false;
        this.invalidRejectReason = markControlAsInvalid;
      }
      if (this.criteriaColumns.filter((c) => c.partialSelected).length < 1) {
        formValid = false;
        this.invalidDecisionCriteria = markControlAsInvalid;
      }
    }

    if (this.hasPredictionEngineResult) {
      if (
        this.hasFunctionPrediction &&
        this.comparable.isFunctionPredictionMarkedAsComparable === null &&
        (this.isUserPredictionDecisionRequired || this.comparable.isProductPredictionMarkedAsComparable !== null)
      ) {
        formValid = false;
        this.invalidPredictionResultFunctionDecision = markControlAsInvalid;
      }
      if (
        this.hasProductPrediction &&
        this.comparable.isProductPredictionMarkedAsComparable === null &&
        (this.isUserPredictionDecisionRequired || this.comparable.isFunctionPredictionMarkedAsComparable !== null)
      ) {
        formValid = false;
        this.invalidPredictionResultProductDecision = markControlAsInvalid;
      }
    }

    return formValid;
  }

  get hasPredictionEngineResult(): boolean {
    return !!this.comparable && this.comparable.functionPrediction !== null && this.comparable.productPrediction !== null;
  }

  async saveAcceptanceOfPrediction() {
    if (!this.hasPredictionEngineResult) {
      return;
    }
    if (this.comparable.isFunctionPredictionMarkedAsComparable === null && this.comparable.isProductPredictionMarkedAsComparable === null) {
      return;
    }

    const predictionAcceptance = new PredictionAcceptance();
    predictionAcceptance.searchStrategyId = this.searchStrategyId;
    predictionAcceptance.comparableIds.push(this.comparable.comparableID);
    predictionAcceptance.isFunctionPredictionMarkedAsComparable = this.comparable.isFunctionPredictionMarkedAsComparable;
    predictionAcceptance.isProductPredictionMarkedAsComparable = this.comparable.isProductPredictionMarkedAsComparable;
    predictionAcceptance.invalidateFunctionPredictionRerunInfo = true;
    predictionAcceptance.invalidateProductPredictionRerunInfo = true;

    await this.predictionEngineService.saveAcceptanceOfPrediction(predictionAcceptance);
  }

  onFunctionDecisionTypeChanged(): void {
    this.formChanged('funcDecision');

    const isSelected = !this.comparable.isFunctionPredictionMarkedAsComparable;

    const item = _.find(this.decisionCriteriaColumns, ['displayOrder', this.comparableFunctionDisplayOrderNumber]);
    const treeNode: TreeNode = this.convertEliminationCriteriaToTreeNode(item, isSelected, this.readOnly);

    const index = _.findIndex(this.criteriaColumns, (m) => m.data.displayOrder == this.comparableFunctionDisplayOrderNumber);
    this.criteriaColumns[index] = treeNode;

    this.formChanged('decisionCriteria');
  }

  onProductDecisionTypeChanged(): void {
    this.formChanged('productDecision');

    const isSelected = !this.comparable.isProductPredictionMarkedAsComparable;

    const item = _.find(this.decisionCriteriaColumns, ['displayOrder', this.comparableProductDisplayOrderNumber]);
    const treeNode: TreeNode = this.convertEliminationCriteriaToTreeNode(item, isSelected, this.readOnly);

    const index = _.findIndex(this.criteriaColumns, (m) => m.data.displayOrder == this.comparableProductDisplayOrderNumber);
    this.criteriaColumns[index] = treeNode;

    this.formChanged('decisionCriteria');
  }

  async databaseSelected() {
    this.blockui = true;
    await this.getNonBvdComparableDetails();
    this.blockui = false;
  }

  private async getComparableDatabases() {
    if (!this.isMultiDB) {
      return;
    }

    const comparableDatabaseFilter: ComparableDatabaseFilter = new ComparableDatabaseFilter();
    comparableDatabaseFilter.studySearchStrategyId = this.searchStrategyId;
    comparableDatabaseFilter.comparableId = this.comparable.comparableID;
    this.comparableDatabases = await this.bdMatrixService.GetComparableDatabases(comparableDatabaseFilter);
    this.selectedDatabase = _.find(this.comparableDatabases, ['databaseId', this.databaseId]);
  }

  onFunctionDecisionTypeChangedFromCheckbox(rowData) {
    if (rowData.data.displayOrder === this.comparableFunctionDisplayOrderNumber) {
      this.comparable.isFunctionPredictionMarkedAsComparable = !this.comparable.isFunctionPredictionMarkedAsComparable;
      this.onFunctionDecisionTypeChanged();
    } else if (rowData.data.displayOrder === this.comparableProductDisplayOrderNumber) {
      this.comparable.isProductPredictionMarkedAsComparable = !this.comparable.isProductPredictionMarkedAsComparable;
      this.onProductDecisionTypeChanged();
    } else {
      this.formChanged('decisionCriteria');
    }
  }

  isAIDecisionColumn(rowData: any): boolean {
    if (rowData.data.displayOrder === 1 && this.comparable.functionPrediction === 0) {
      return true;
    }
    if (rowData.data.displayOrder === 2 && this.comparable.productPrediction === 0) {
      return true;
    }
    return false;
  }
}
